import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

import { dockReducer, initialDockState, type DockState } from './dock-reducer';

export interface MessagingDockContextValue {
  readonly state: DockState;
  /** Bubble click: toggle the recent-conversations list launcher. */
  readonly toggleLauncher: () => void;
  /** Show the launcher list (no-op when already shown). */
  readonly openLauncher: () => void;
  /** Open a conversation window (or focus + restore it if already open). */
  readonly openThread: (conversationId: string) => void;
  /** Bring an already-open window to the front; restores it if minimized. */
  readonly focusWindow: (conversationId: string) => void;
  /** Collapse a window to a chat head (stays mounted, drafts/scroll preserved). */
  readonly minimizeWindow: (conversationId: string) => void;
  /** Close a window entirely. */
  readonly closeWindow: (conversationId: string) => void;
  /** Unsent draft text for a conversation, restored when its composer remounts. */
  readonly getDraft: (conversationId: string) => string;
  /** Persist (or clear, on '') a conversation's unsent draft. */
  readonly setDraft: (conversationId: string, text: string) => void;
}

const MessagingDockContext = createContext<MessagingDockContextValue | null>(null);

// Mounted above BOTH the `_authed` and `_public` layout routes (in main.tsx)
// so the dock's window collection survives every navigation — including the
// `_authed ↔ _public` boundary, which remounts the Shell. The dock VIEW lives
// in Shell; this provider holds only the UI state, so it needs no viewer or
// query context of its own.
export function MessagingDockProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(dockReducer, initialDockState);

  // Unsent drafts, keyed by conversationId. A ref (not state) on purpose: this
  // provider wraps the router, so keystroke-frequency setState here would
  // re-render the whole app. The composer only reads a draft on mount
  // (`initialDraft`) and writes on change, so a mutable ref is sufficient and
  // survives the composer's unmount (minimize/close) and app navigation.
  const draftsRef = useRef<Record<string, string>>({});
  // Stable identities: each window freezes its `initialDraft` on
  // `getDraft`/`conversationId` at mount, so a shifting identity here would
  // re-seed a composer mid-typing whenever any OTHER window changed dock state.
  const getDraft = useCallback(
    (conversationId: string) => draftsRef.current[conversationId] ?? '',
    [],
  );
  const setDraft = useCallback((conversationId: string, text: string) => {
    if (text.length === 0) {
      delete draftsRef.current[conversationId];
    } else {
      draftsRef.current[conversationId] = text;
    }
  }, []);

  const value = useMemo<MessagingDockContextValue>(
    () => ({
      state,
      toggleLauncher: () => dispatch({ type: 'toggleLauncher' }),
      openLauncher: () => dispatch({ type: 'openLauncher' }),
      openThread: (conversationId: string) => dispatch({ type: 'openThread', conversationId }),
      focusWindow: (conversationId: string) => dispatch({ type: 'focusWindow', conversationId }),
      minimizeWindow: (conversationId: string) =>
        dispatch({ type: 'minimizeWindow', conversationId }),
      closeWindow: (conversationId: string) => dispatch({ type: 'closeWindow', conversationId }),
      getDraft,
      setDraft,
    }),
    [state, getDraft, setDraft],
  );

  return (
    <MessagingDockContext.Provider value={value}>{children}</MessagingDockContext.Provider>
  );
}

export function useMessagingDock(): MessagingDockContextValue {
  const ctx = useContext(MessagingDockContext);
  if (ctx === null) {
    throw new Error(
      'useMessagingDock must be used within a <MessagingDockProvider>. The provider is mounted above both layout routes in main.tsx.',
    );
  }
  return ctx;
}
